import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircleOutlined } from '@ant-design/icons';
import Cookies from 'universal-cookie';
import { selectUser, checkAuth } from 'components/AuthStore';
import FaceLoginModal from './FaceLoginModal';
import { faceRegister } from './services';

const cookies = new Cookies();

const CONFETTI_ITEMS = [
  { color: '#FFD700', left: '8%',  top: '-8px',  delay: '0s',    dur: '1.1s'  },
  { color: '#FF6B6B', left: '22%', top: '-4px',  delay: '0.08s', dur: '1.3s'  },
  { color: '#4ECDC4', left: '38%', top: '-10px', delay: '0.04s', dur: '1s'    },
  { color: '#7C83FD', left: '52%', top: '-6px',  delay: '0.12s', dur: '1.2s'  },
  { color: '#96CEB4', left: '67%', top: '-8px',  delay: '0.16s', dur: '0.95s' },
  { color: '#FF9A3C', left: '80%', top: '-4px',  delay: '0.06s', dur: '1.15s' },
  { color: '#F7D794', left: '92%', top: '-10px', delay: '0.2s',  dur: '1.05s' }
];

export function SuccessModal({ open, onClose }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable
      centered
      width={380}
      zIndex={10002}>
      <style>{`
        @keyframes faceReg_confettiFall {
          0%   { transform: translateY(0)    rotate(0deg);   opacity: 1; }
          100% { transform: translateY(70px) rotate(420deg); opacity: 0; }
        }
        @keyframes faceReg_scaleIn {
          0%   { transform: scale(0);    opacity: 0; }
          60%  { transform: scale(1.2); }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes faceReg_slideUp {
          0%   { transform: translateY(16px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
        @keyframes faceReg_ring {
          0%   { transform: scale(1);   opacity: 0.7; }
          100% { transform: scale(1.9); opacity: 0;   }
        }
      `}</style>

      <div
        style={{
          textAlign: 'center',
          padding: '28px 16px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}>
        {/* Confetti */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', pointerEvents: 'none' }}>
          {CONFETTI_ITEMS.map((c, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: c.left,
                top: c.top,
                width: 8,
                height: 8,
                borderRadius: 2,
                background: c.color,
                animation: `faceReg_confettiFall ${c.dur} ${c.delay} ease-in both`
              }}
            />
          ))}
        </div>

        {/* Check icon + ring pulse */}
        <div
          style={{
            animation: 'faceReg_scaleIn 0.5s 0.1s ease-out both',
            marginBottom: 16,
            position: 'relative',
            display: 'inline-block'
          }}>
          {/* ring 1 */}
          <span
            style={{
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              border: '3px solid #52c41a',
              animation: 'faceReg_ring 1.2s 0.6s ease-out both'
            }}
          />
          {/* ring 2 */}
          <span
            style={{
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              border: '2px solid #52c41a',
              animation: 'faceReg_ring 1.2s 0.85s ease-out both'
            }}
          />
          <CheckCircleOutlined
            style={{ fontSize: 64, color: '#52c41a', display: 'block' }}
          />
        </div>

        {/* Text */}
        <div style={{ animation: 'faceReg_slideUp 0.4s 0.35s ease-out both' }}>
          <h3
            style={{
              fontWeight: 700,
              color: '#ffa300',
              marginBottom: 8,
              fontSize: 17
            }}>
            Đăng ký khuôn mặt thành công!
          </h3>
          <p style={{ color: '#666', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
            Bạn có thể đăng nhập bằng khuôn mặt trong các lần tiếp theo.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '9px 32px',
              border: 'none',
              borderRadius: 20,
              background: '#ffa300',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer'
            }}>
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Inline SVG — không dùng lucide-react
function ScanFaceIcon({ size = 48, color = '#ffa300' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', margin: '0 auto' }}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 16.8a7 7 0 0 1 10 0" />
    </svg>
  );
}

const FACE_REG_PROMPTED_KEY = 'face_reg_prompted';

const FaceRegisterPrompt = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [openPrompt, setOpenPrompt] = useState(false);
  const [openFaceRegister, setOpenFaceRegister] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    const alreadyPrompted = localStorage.getItem(FACE_REG_PROMPTED_KEY);
    if (alreadyPrompted) return;
    if (!user.face_login_registered) {
      const timer = setTimeout(() => setOpenPrompt(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    localStorage.setItem(FACE_REG_PROMPTED_KEY, '1');
    setOpenPrompt(false);
  };

  const handleRegisterNow = () => {
    dismiss();
    setOpenFaceRegister(true);
  };

  const handleCapture = useCallback(
    async (file) => {
      if (!file) return;
      setLoading(true);
      setErrorMessage('');

      const res = await faceRegister(file, (err) => {
        const msg =
          err?.response?.data?.errors?.[0]?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Đăng ký khuôn mặt thất bại. Vui lòng thử lại.';
        setErrorMessage(msg);
      });

      setLoading(false);

      if (res && res.status === 200) {
        const userStore = cookies.get('user_store');
        if (userStore) {
          const updatedUser = { ...userStore, face_login_registered: true };
          cookies.set(
            'user_store',
            JSON.stringify(updatedUser).replace(/</g, '\\u003c'),
            { path: '/' }
          );
        }
        if (user?.id) {
          localStorage.setItem('face_login_user_id', String(user.id));
          localStorage.setItem(
            'face_login_user_name',
            user.fullname || user.username || ''
          );
        }
        localStorage.removeItem('user_cache_timestamp');
        localStorage.removeItem('cached_user_data');
        localStorage.removeItem('cached_access_token');
        localStorage.removeItem('cached_data_hash');
        dispatch(checkAuth());

        setOpenFaceRegister(false);
        setOpenSuccess(true);
      }
    },
    [dispatch, user]
  );

  return (
    <>
      <Modal
        open={openPrompt}
        onCancel={dismiss}
        footer={null}
        closable={false}
        centered
        width={420}
        zIndex={10001}>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <ScanFaceIcon size={48} color="#ffa300" />
          <h3
            style={{
              fontWeight: 700,
              color: '#ffa300',
              marginBottom: 8,
              marginTop: 16
            }}>
            Chưa đăng ký khuôn mặt
          </h3>
          <p style={{ color: '#555', marginBottom: 24 }}>
            Hãy đăng ký để đăng nhập nhanh và an toàn bằng khuôn mặt
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={dismiss}
              style={{
                padding: '8px 24px',
                border: '1px solid #d9d9d9',
                borderRadius: 20,
                background: '#fff',
                cursor: 'pointer',
                fontWeight: 500
              }}>
              Để sau
            </button>
            <button
              onClick={handleRegisterNow}
              style={{
                padding: '8px 24px',
                border: 'none',
                borderRadius: 20,
                background: '#ffa300',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600
              }}>
              Đăng ký ngay
            </button>
          </div>
        </div>
      </Modal>

      <FaceLoginModal
        open={openFaceRegister}
        onClose={() => {
          setOpenFaceRegister(false);
          setErrorMessage('');
          setLoading(false);
        }}
        onCapture={handleCapture}
        loading={loading}
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage('')}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Đăng ký khuôn mặt
            {loading && (
              <Tag color="processing" style={{ fontWeight: 400, fontSize: 12 }}>
                Đang đăng ký...
              </Tag>
            )}
          </span>
        }
        confirmText="Đăng ký"
        loadingText="Đang đăng ký..."
      />

      <SuccessModal open={openSuccess} onClose={() => setOpenSuccess(false)} />
    </>
  );
};

export default FaceRegisterPrompt;
